/**
 * Extracts dealer information from a row data object using a path string
 * @param rowData The data object to extract from
 * @param path Dot-notation path to the dealer object (e.g., "car.relation")
 * @returns Dealer info with id and name, or null if not found
 */
export const extractDealerInfo = (
  rowData: Record<string, unknown>,
  path: string,
): { id: string; name: string } | null => {
  if (!path) return null;

  const pathSegments = path.split(".");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let current: any = rowData;

  // Navigate through the path segments
  for (const segment of pathSegments) {
    if (
      current &&
      typeof current === "object" &&
      segment in (current as Record<string, unknown>)
    ) {
      current = (current as Record<string, unknown>)[segment];
    } else {
      return null; // Path doesn't exist
    }
  }

  // Check if the final value has id and name properties
  if (
    current &&
    typeof current === "object" &&
    "id" in (current as Record<string, unknown>) &&
    "name" in (current as Record<string, unknown>)
  ) {
    const dealer = current as { id: unknown; name: unknown };
    if (
      typeof dealer.id === "string" &&
      typeof dealer.name === "string" &&
      dealer.id &&
      dealer.name
    ) {
      // Special handling for storedLatestRequest
      if (
        rowData.storedLatestRequest &&
        typeof rowData.storedLatestRequest === "object" &&
        rowData.storedLatestRequest !== null &&
        "relation" in rowData.storedLatestRequest
      ) {
        const latestRequest = rowData.storedLatestRequest as Record<
          string,
          unknown
        >;
        const latestRelation = latestRequest.relation;

        if (
          latestRelation &&
          typeof latestRelation === "object" &&
          "id" in latestRelation &&
          "name" in latestRelation
        ) {
          const relation = latestRelation as {
            id: unknown;
            name: unknown;
          };
          if (
            typeof relation.id === "string" &&
            typeof relation.name === "string"
          ) {
            return { id: relation.id, name: relation.name };
          }
        }
      }

      return { id: dealer.id, name: dealer.name };
    }
  }

  return null;
};
